import React, { useEffect, useState } from 'react';
import { ArrowRight, Bug, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ResolvedPeriod } from '../PeriodPicker';
import { BugTypeRow, QualityApi } from './qualityApi';
import { DrillDownModal } from './DrillDownModal';
import { useEstimationUnit } from '../estimation/estimationUnit';
import { EstimationUnitToggle } from '../estimation/EstimationUnitToggle';

interface BugTypeBreakdownProps {
  resolvedPeriod: ResolvedPeriod | null;
}

export const BugTypeBreakdown: React.FC<BugTypeBreakdownProps> = ({ resolvedPeriod }) => {
  const [rows, setRows] = useState<BugTypeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ value: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      setErrorCode(null);
      try {
        const data = await QualityApi.getBugTypes({ period: null, resolvedPeriod });
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          const asAny = err as { code?: string; message?: string } | undefined;
          setError(asAny?.message || 'Failed to load Bug Type breakdown');
          setErrorCode(asAny?.code || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the resolved date bounds change — object identity
    // of `resolvedPeriod` flips on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  const total = rows.reduce((s, r) => s + r.count, 0);
  const { parenSuffix, toUnitValue } = useEstimationUnit();

  return (
    <div className="chart-card quality-bugtype-card">
      <div className="chart-header chart-header--with-toolbar">
        <div className="chart-header__main">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bug size={18} />
            By Bug Type
            <span
              className="stat-hint-icon"
              title="Breakdown of bugs by your Jira Bug Type custom field. Useful to separate production regressions from new-feature defects or tech-debt items. Configure the classifier in Settings → Quality."
              aria-label="About Bug Type breakdown"
            >
              <Info size={14} />
            </span>
          </h3>
          <p>Total: {loading ? '…' : total}</p>
        </div>
        <EstimationUnitToggle />
      </div>
      {loading ? (
        <p className="quality-breakdown__empty">Loading…</p>
      ) : errorCode === 'quality.bug_type_field_not_configured' ||
        (error && /classifier|bug type/i.test(error)) ? (
        <div className="quality-breakdown__empty dynamics-empty-diagnostic">
          <p>
            <strong>Bug Type classifier field is not configured.</strong>
          </p>
          <p>
            Pick the Jira field that classifies your bugs (e.g. <em>Bug Type</em>,<em> Cluster</em>,{' '}
            <em>Defect Category</em>) so this card can group them.
          </p>
          <p style={{ marginTop: 10 }}>
            <Link
              to="/settings?tab=project&section=quality-settings"
              className="btn btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 6,
                textDecoration: 'none',
              }}
            >
              Set up in Settings → Quality
              <ArrowRight size={14} />
            </Link>
          </p>
        </div>
      ) : error ? (
        <p className="quality-breakdown__empty">Failed to load: {error}</p>
      ) : rows.length === 0 ? (
        <p className="quality-breakdown__empty">
          No Bug Type values in this Period. Either no bugs have a value for this field yet, or you
          need to run <strong>Sync now</strong> for this field in{' '}
          <Link to="/settings#profile-forecast-custom-fields">
            Settings → Forecast custom fields
          </Link>{' '}
          to backfill values from Jira.
        </p>
      ) : (
        <table className="estimation-table">
          <thead>
            <tr>
              <th>Bug Type</th>
              <th>Count</th>
              <th>% of total</th>
              <th>Avg MTTR {parenSuffix}</th>
              <th>Top component</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.bugType}
                className="quality-bugtype-row"
                onClick={() => setDrill({ value: r.bugType })}
              >
                <td>{r.bugType}</td>
                <td>{r.count}</td>
                <td>{r.percentOfTotal.toFixed(1)}%</td>
                <td>{r.avgMttrHours != null ? toUnitValue(r.avgMttrHours).toFixed(1) : '—'}</td>
                <td>{r.topComponent || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {drill && (
        <DrillDownModal
          by="bugType"
          value={drill.value}
          resolvedPeriod={resolvedPeriod}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};
